import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Clock, TrendingUp, User as UserIcon, Gavel } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuctionDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [auction, setAuction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bidAmount, setBidAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchAuction();
    const interval = setInterval(fetchAuction, 5000); // Refresh every 5 seconds
    return () => clearInterval(interval);
  }, [id]);

  const fetchAuction = async () => {
    try {
      const response = await axios.get(`${API}/auctions/${id}`);
      setAuction(response.data);
      if (!bidAmount) {
        setBidAmount((response.data.current_bid + 100).toString());
      }
    } catch (error) {
      console.error('Failed to fetch auction:', error);
      toast.error('Auction not found');
      navigate('/auctions');
    } finally {
      setLoading(false);
    }
  };

  const handlePlaceBid = async () => {
    if (!bidAmount || parseFloat(bidAmount) <= auction.current_bid) {
      toast.error(`Bid must be higher than current bid of ₹${auction.current_bid}`);
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${API}/auctions/${id}/bid?bid_amount=${bidAmount}`,
        {},
        { withCredentials: true }
      );
      toast.success('Bid placed successfully!');
      fetchAuction();
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Please login to place a bid');
        const redirectUrl = `${window.location.origin}/auth-callback`;
        window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/google/login`;
      } else {
        toast.error(error.response?.data?.detail || 'Failed to place bid');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const getTimeRemaining = (endTime) => {
    const now = new Date();
    const end = new Date(endTime);
    const diff = end - now;

    if (diff <= 0) return 'Auction Ended';

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    if (days > 0) return `${days}d ${hours}h ${minutes}m`;
    if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
    return `${minutes}m ${seconds}s`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center" data-testid="loading">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-red-600"></div>
      </div>
    );
  }

  if (!auction) return null;

  return (
    <div className="min-h-screen bg-black" data-testid="auction-detail-page">
      <Navbar />
      
      <div className="pt-24 px-4 sm:px-6 lg:px-8 pb-20">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Auction Image & Info */}
            <div className="space-y-6">
              <div className="relative rounded-2xl overflow-hidden" data-testid="auction-image">
                <img 
                  src={auction.product?.image_url || 'https://images.unsplash.com/photo-1608278047522-58806a6ac85b'} 
                  alt={auction.product?.name} 
                  className="w-full h-auto object-cover" 
                />
                <div className="absolute top-4 left-4 bg-red-600 text-white px-4 py-2 rounded-full text-sm font-semibold flex items-center space-x-2 pulse-red">
                  <span className="w-3 h-3 bg-white rounded-full animate-pulse"></span>
                  <span>LIVE AUCTION</span>
                </div>
              </div>

              {/* Product Details */}
              <div className="glass-effect p-6 rounded-xl">
                <h2 className="text-2xl font-bold mb-4">About This Item</h2>
                <p className="text-gray-400 mb-4" data-testid="product-description">
                  {auction.product?.description || 'Exclusive collectible item up for auction'}
                </p>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-400">Category</p>
                    <p className="font-semibold" data-testid="product-category">{auction.product?.category}</p>
                  </div>
                  <div>
                    <p className="text-gray-400">Type</p>
                    <p className="font-semibold" data-testid="product-type">{auction.product?.type}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Bidding Section */}
            <div className="space-y-6">
              <div>
                <h1 className="text-4xl font-bold mb-2" data-testid="auction-title">{auction.product?.name}</h1>
                <p className="text-gray-400">Auction ID: {auction.id.slice(0, 8)}</p>
              </div>

              {/* Time Remaining */}
              <div className="glass-effect p-6 rounded-xl">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-400">Time Remaining</span>
                  <Clock className="text-red-500" size={20} />
                </div>
                <p className="text-3xl font-bold accent-red" data-testid="time-remaining">
                  {getTimeRemaining(auction.end_time)}
                </p>
              </div>

              {/* Current Bid */}
              <div className="glass-effect p-6 rounded-xl">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-400">Current Bid</span>
                  <TrendingUp className="text-red-500" size={20} />
                </div>
                <p className="text-5xl font-bold accent-red mb-2" data-testid="current-bid">
                  ₹{auction.current_bid.toLocaleString()}
                </p>
                <p className="text-sm text-gray-400">Starting bid: ₹{auction.starting_price.toLocaleString()}</p>
              </div>

              {/* Place Bid */}
              {auction.status === 'active' && (
                <div className="glass-effect p-6 rounded-xl">
                  <h3 className="text-xl font-bold mb-4 flex items-center space-x-2">
                    <Gavel className="text-red-500" size={24} />
                    <span>Place Your Bid</span>
                  </h3>
                  
                  <div className="mb-4">
                    <label className="block text-sm font-semibold mb-2">Bid Amount (₹)</label>
                    <input
                      type="number"
                      value={bidAmount}
                      onChange={(e) => setBidAmount(e.target.value)}
                      placeholder="Enter your bid"
                      min={auction.current_bid + 1}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white text-lg focus:outline-none focus:border-red-500"
                      data-testid="bid-input"
                    />
                    <p className="text-sm text-gray-400 mt-2">
                      Minimum bid: ₹{(auction.current_bid + 100).toLocaleString()}
                    </p>
                  </div>

                  <button
                    onClick={handlePlaceBid}
                    disabled={submitting}
                    className="w-full btn-primary py-4 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="place-bid-btn"
                  >
                    {submitting ? 'Placing Bid...' : 'Place Bid'}
                  </button>
                </div>
              )}

              {/* Bid History */}
              <div className="glass-effect p-6 rounded-xl">
                <h3 className="text-xl font-bold mb-4">Bid History</h3>
                {auction.bids && auction.bids.length > 0 ? (
                  <div className="space-y-3 max-h-80 overflow-y-auto" data-testid="bid-history">
                    {auction.bids.map((bid, index) => (
                      <div key={bid.id} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                        <div className="flex items-center space-x-3">
                          {bid.user?.picture ? (
                            <img src={bid.user.picture} alt={bid.user.name} className="w-10 h-10 rounded-full" />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-red-600 flex items-center justify-center">
                              <UserIcon size={20} />
                            </div>
                          )}
                          <div>
                            <p className="font-semibold">{bid.user?.name || 'Anonymous'}</p>
                            <p className="text-xs text-gray-400">
                              {new Date(bid.created_at).toLocaleString()}
                            </p>
                          </div>
                        </div>
                        <p className="text-xl font-bold accent-red">₹{bid.bid_amount.toLocaleString()}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-400 text-center py-8" data-testid="no-bids">No bids yet. Be the first!</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AuctionDetail;